import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import { useMemo } from 'react';
import styled from 'styled-components';
import { TPaymentPeriodPayment } from '../../../../../types/payment-data-types';
import { ILineItem } from '../../../../../types/tax-summary-types';
import { TSupportedLocales } from '../../../utils';
import { LineItem, TabulatedLineItems } from './line-item';

const Div = styled.div`
  margin: 15px 0;
  border-top: 1px solid black;
  border-bottom: 1px solid black;
`;

const PaymentPeriodLabel = styled.p`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

function gatherSortedLineItemsFn(
  data: TPaymentPeriodPayment,
  order?: string[],
) {
  const dataStoreSortedByProviderOrder =
    !isNil(order) && !isEmpty(order)
      ? cloneDeep(data.taxableActivities).sort((a, b) => order.indexOf(a.provider) - order.indexOf(b.provider))
      : cloneDeep(data.taxableActivities);

  return (field: string, useFullData?: boolean) => {
    return useFullData ?
      [get(data, field)] :
      dataStoreSortedByProviderOrder.map((pData) => get(pData, field));
  }
}

export function PaymentPeriodSummary(props: {
  label?: string;
  locale: TSupportedLocales;
  data: TPaymentPeriodPayment;
  providerLineItems: string[];
  providerOrder: string[];
  lineItems: ILineItem[];
}) {
  const { locale, data, providerLineItems, providerOrder, lineItems } = props;

  const tabulatedLinedItems = useMemo(() => {
    const gatherSortedLineItems = gatherSortedLineItemsFn(data, providerOrder);
    return lineItems.map(({ lineItems, totalLineItem }) => ({
      assembledLineItems: lineItems.map((item) =>
        merge({}, item, {
          items: gatherSortedLineItems(item.field, item.useFullData),
        }),
      ),
      assembledTotalLineItem: merge({}, totalLineItem, {
        items: gatherSortedLineItems(
          totalLineItem.field,
          totalLineItem.useFullData,
        ),
      }),
    }));
  }, [data, lineItems, providerOrder]);

  return (
    <div>
      <PaymentPeriodLabel>{data.label}</PaymentPeriodLabel>

      {Boolean(providerLineItems.length) && (
        <div style={{ marginBottom: '10px' }}>
          <LineItem header="" items={providerLineItems} locale={locale} />
        </div>
      )}

      <Block>
        {tabulatedLinedItems.map((el, i) => (
          <TabulatedLineItems key={i} {...el} paymentPeriodLabel={data.period} locale={locale} />
        ))}
      </Block>
    </div>
  );
}